import {
  CS_UI_LANGUAGE_KEY,
  CS_UI_LANGUAGE_UID_KEY,
  CS_UI_LANGUAGE_UNCONFIRMED_KEY,
  UiLanguageReconcileDecision,
  decideUiLanguageReconcile,
  uiLanguageStorageOpsFor,
} from 'i18n/ui-language';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import type { Locales } from '../../gensrc/i18n/i18n-types';

/**
 * Session-scoped marker saying this tab has already reloaded once to change the
 * visible language. It is what makes the reload unrepeatable, so a reconcile can
 * never turn into a reload loop.
 */
export const UI_LANGUAGE_RECONCILED_KEY = 'cs_ui_language_reconciled';

/** The part of the browser's `localStorage` / `sessionStorage` this needs. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Everything outside this class that a reconcile touches. It is one interface
 * rather than several injected services so a unit test can drive the whole
 * reconcile with one plain object — there is no browser, no account and no
 * reload in the test runner.
 */
export interface UiLanguageReconcileEnvironment {
  /**
   * Device-scoped storage. Every key this class reads or writes there
   * deliberately SURVIVES sign-out — that is the whole point, because the case
   * this reconcile exists for is one person signing out and another signing in
   * on the same device. See the device-key notes in i18n/ui-language.ts.
   */
  local: KeyValueStore;
  /**
   * Tab-scoped storage. Holds the one-reload marker above, and nothing else.
   * It has to be tab-scoped: it must survive a reload in this tab, and it must
   * NOT survive into a new session, or the app could only ever reconcile once.
   */
  session: KeyValueStore;
  /** The per-user language preference that syncs with the account. */
  readSavedPreference(): string | undefined;
  /** Set the per-user preference in memory. Paired with saveSavedPreferenceAsync. */
  writeSavedPreference(locale: Locales): void;
  /** Persist whatever writeSavedPreference just set. */
  saveSavedPreferenceAsync(): Promise<void>;
  /** Report a condition somebody may eventually need to act on. */
  reportProblem(name: string, context: Record<string, unknown>): void;
  /** Report a caught exception. */
  reportException(err: unknown): void;
}

/**
 * Sign-in reconciliation for the UI language (#4147).
 *
 * The device-scoped `cs_ui_language` key is a per-DEVICE boot cache; the
 * authoritative preference is the per-USER `settings.uiLanguage`, which has just
 * synced down. THE RULE ITSELF — including why an empty saved preference is
 * ambiguous and how `cs_ui_language_uid` resolves it — lives in
 * `decideUiLanguageReconcile()` (pure and unit-tested, i18n/ui-language.ts), and
 * is written up in design-docs/i18n-translation.md. This class only gathers
 * browser state, carries out the verdict, and reloads ONCE when the visible
 * language must change.
 *
 * #4284 note: this path deliberately does NOT pre-sync the target language's
 * curated `global_common` content, and must not grow a splash-blocking sync.
 * After the reload, `global_common` was synced seconds ago so the initial sync
 * takes the recently-synced fast-path (live sync, not pass-through); the new
 * locale's `$text/` documents arrive on that live pull and the exercise
 * DocWatcher re-reads when they land. Until then help/exercise render the
 * English fallback (never blank), and an offline reconcile simply stays English
 * for the session and self-heals on reconnect. The Settings language switch is
 * the path that pre-syncs (it can fail the switch); a cross-device reconcile
 * cannot, so it self-heals instead.
 *
 * This is the plain half of `UiLanguageReconcileService`, which supplies the
 * real browser storage, the real account settings and the real reload. It is
 * split out so the ordering rules below can be unit tested: none of those three
 * things exists in the node test runner, and the two orderings that matter here
 * were both real bugs — the saved preference must be read BEFORE the heal
 * overwrites it, and every storage write must sit BELOW the one-reload guard.
 *
 * Lifted out of `AppComponent` unchanged.
 */
export class UiLanguageReconciler {
  constructor(private env: UiLanguageReconcileEnvironment) {}

  /**
   * Carry out the device-storage writes a reconcile verdict implies (see
   * uiLanguageStorageOpsFor, which returns them as testable data).
   *
   * Private on purpose. Two of the three places these writes belong sit BELOW
   * the one-reload guard in the method beneath, and a caller reaching them from
   * outside would skip it — which is the ordering bug this class documents.
   */
  private applyStorageOps(decision: UiLanguageReconcileDecision, currentUid: string): void {
    for (const op of uiLanguageStorageOpsFor(decision, currentUid)) {
      if (op.op === 'set') {
        this.env.local.setItem(op.key, op.value);
      } else {
        this.env.local.removeItem(op.key);
      }
    }
  }

  /**
   * Run the reconcile for the signed-in user.
   *
   * `boundLocale` must be the locale the translations are ACTUALLY bound to —
   * captured once when the translation module loaded — and NOT a fresh read of
   * the device key. A re-read can have been overwritten since module load (the
   * first-run default writes the key mid-boot), which would falsely match the
   * target and skip a reload the translations still need. That is why it is a
   * parameter rather than something this class reads for itself.
   */
  reconcileAfterSettingsSync(
    currentUid: string,
    boundLocale: Locales,
    environmentDefault: Locales,
    requestReload: () => void,
  ): void {
    // The decision itself is a pure function so every branch is unit-tested —
    // see decideUiLanguageReconcile in i18n/ui-language.ts. This method only
    // gathers the browser state it needs and carries out the verdict.
    const decision = decideUiLanguageReconcile({
      savedPreference: this.env.readSavedPreference(),
      deviceKey: this.env.local.getItem(CS_UI_LANGUAGE_KEY),
      deviceKeyOwnerUid: this.env.local.getItem(CS_UI_LANGUAGE_UID_KEY),
      unconfirmedChoice: this.env.local.getItem(CS_UI_LANGUAGE_UNCONFIRMED_KEY),
      currentUid,
      boundLocale,
      environmentDefault,
    });

    // Always log. This reconciliation used to be completely silent, which is why
    // a language switch that reverted itself had to be diagnosed by reading code
    // rather than logs.
    console.log(`Locale: reconcile — ${decision.reason}`);

    if (decision.action === 'keep') {
      this.applyStorageOps(decision, currentUid);
      if (decision.healSavedPreferenceTo) {
        // This device holds a language the user chose here that their account does
        // not reflect — see `healSavedPreferenceTo` for when that happens and why
        // it may overwrite a non-empty preference. Without the heal the device
        // keeps them in their language while the ACCOUNT forgets it, so the choice
        // stops following them and vanishes when the device key is reset.
        // Captured BEFORE the heal overwrites it — reading it afterwards would just
        // echo the healed language back and the two shapes below could never be
        // told apart.
        const savedPreferenceBeforeHeal = this.env.readSavedPreference() || '(empty)';
        this.env.writeSavedPreference(decision.healSavedPreferenceTo);
        this.env.saveSavedPreferenceAsync().catch(err => {
          // our own teardown (sign-out, Delete Private Data, an account switch) is
          // expected, not a fault: the language is already on this device and is
          // saved again next time. console.log, not console.error, so it cannot trip
          // the E2E unexpected-browser-error gate. Anything else is still reported.
          if (isDatabaseTornDown(err)) {
            console.log('Locale: reconcile — the healed language was not saved to the account because the database is shutting down; it is already on this device');
            return;
          }
          this.env.reportException(err);
        });
        // Surface it: a settings write that did not land is an abnormal condition,
        // and a console line is invisible in production — which is exactly why the
        // bug this class exists to fix could not be diagnosed from telemetry.
        // `savedPreference` separates the two shapes: empty means the write never
        // arrived, a different locale means the account is actively disagreeing.
        this.env.reportProblem('UiLanguageNotSyncedToSettings', {
          language: decision.healSavedPreferenceTo,
          savedPreference: savedPreferenceBeforeHeal,
        });
      }
      return;
    }

    // One reload per session — this cannot loop even if something re-triggers it.
    if (this.env.session.getItem(UI_LANGUAGE_RECONCILED_KEY) != null) {
      console.log('Locale: reconcile — already reconciled once this session, not reloading again');
      return;
    }

    // ALL of these live below the one-reload guard on purpose. If the guard
    // returned above, the device key still holds the previous language — and an
    // ownership record or a cleared marker for a key we did not write is exactly
    // how a foreign language gets mistaken for this user's own choice later.
    this.applyStorageOps(decision, currentUid);
    this.env.local.setItem(CS_UI_LANGUAGE_KEY, decision.target);
    this.env.session.setItem(UI_LANGUAGE_RECONCILED_KEY, '1');
    // Reload so the module-level translations rebind to the reconciled locale.
    // We are still behind the boot splash here, so this extends the splash
    // briefly rather than flashing a wrong-language UI. The reload is passed in
    // per call rather than stored, so it can never be left half-wired.
    requestReload();
  }
}
